import type { LegalDocPair } from '../types'

/**
 * Acuerdo de procesamiento de datos (DPA).
 *
 * Cubre lo que véltica hace por cuenta de los titulares de Sitios: guardar y
 * tratar los datos de sus visitantes, compradores y personas que envían
 * formularios o reservan. Ahí el cliente es el responsable y véltica el
 * encargado. Las cláusulas contractuales tipo de la UE y el anexo del Reino
 * Unido se incorporan por referencia; el abogado debe confirmar las opciones
 * elegidas (ley irlandesa y tribunales de Irlanda en las cláusulas 17 y 18).
 */
export const DPA: LegalDocPair = {
  en: {
    title: 'Data Processing Addendum',
    summary: 'The terms under which véltica processes personal data of your visitors, buyers and customers on your behalf.',
    intro: [
      'This Data Processing Addendum (the “DPA”) is part of the [Terms of Service](doc:terms) between you (the “Customer”) and {operator}, doing business as véltica (“véltica”). It applies to the extent that véltica processes Customer Personal Data on behalf of the Customer in providing the Platform. It reflects the parties’ agreement on the processing of that data.',
    ],
    sections: [
      {
        title: 'Definitions',
        blocks: [
          '“Customer Personal Data” means personal data of End Users that véltica processes on the Customer’s behalf through the Platform, such as data submitted through forms, bookings and orders, contact lists and site statistics of the Customer’s Sites. “Data Protection Laws” means the laws on privacy and data protection that apply to that processing, including, where applicable, the EU General Data Protection Regulation (“GDPR”), the UK GDPR and Data Protection Act 2018, the Swiss Federal Act on Data Protection and the California Consumer Privacy Act as amended (“CCPA”). “Controller”, “processor”, “service provider”, “data subject”, “processing”, “personal data”, “business” and “sell” have the meanings given in those laws.',
        ],
      },
      {
        title: 'Roles and scope',
        blocks: [
          'For Customer Personal Data, the Customer is the controller (or business) and véltica is the processor (or service provider). véltica is the controller of the personal data described in the [Privacy Policy](doc:privacy), which this DPA does not cover.',
        ],
      },
      {
        title: 'Details of the processing',
        blocks: [
          {
            table: {
              head: ['Item', 'Description'],
              rows: [
                ['Subject matter and purpose', 'Providing the Platform: hosting the Customer’s Sites and storing, showing and delivering the data they collect'],
                ['Duration', 'As long as the Customer has an Account, plus the retention periods in the Privacy Policy'],
                ['Data subjects', 'The Customer’s site visitors, buyers, people who submit forms or book, and contacts'],
                ['Types of data', 'Contact details, message content, booking details, order details, and technical data such as country, device type and page views in aggregate'],
                ['Special categories', 'None intended. The Customer must not use the Platform to collect them (see the Terms of Service)'],
                ['Operations', 'Storing, retrieving, transmitting, displaying, emailing and deleting data, as the Customer configures'],
              ],
            },
          },
        ],
      },
      {
        title: 'véltica’s obligations',
        subs: [
          {
            title: 'Instructions',
            blocks: [
              'véltica will process Customer Personal Data only on the Customer’s documented instructions, which are the Agreement and the Customer’s use and configuration of the Platform, unless the law requires otherwise, in which case véltica will inform the Customer where the law allows. If véltica believes an instruction violates Data Protection Laws, it will tell the Customer.',
            ],
          },
          {
            title: 'Confidentiality',
            blocks: [
              'véltica will make sure that the people who process Customer Personal Data are bound by confidentiality obligations.',
            ],
          },
          {
            title: 'Security',
            blocks: [
              'véltica will apply technical and organizational measures appropriate to the risk, at least those in Annex A.',
            ],
          },
          {
            title: 'Assistance',
            blocks: [
              'Taking into account the nature of the processing, véltica will reasonably help the Customer to respond to requests from data subjects and to meet the Customer’s obligations on security, breach notification, impact assessments and prior consultation. If a data subject contacts véltica about Customer Personal Data, véltica will direct them to the Customer where it can.',
            ],
          },
          {
            title: 'Personal data breaches',
            blocks: [
              'véltica will notify the Customer without undue delay, and aim to do so within 72 hours, after becoming aware of a personal data breach affecting Customer Personal Data, with the information it has to help the Customer meet its own obligations.',
            ],
          },
          {
            title: 'Deletion and return',
            blocks: [
              'When the processing ends, véltica will delete Customer Personal Data as described in the Privacy Policy, including after the period in which a deleted project can be restored, unless the law requires it to be kept. Before deletion, the Customer can save the data it needs.',
            ],
          },
          {
            title: 'Demonstrating compliance',
            blocks: [
              'véltica will make available the information reasonably necessary to show its compliance with this DPA and will allow and contribute to reasonable audits by the Customer or its auditor, on reasonable notice, no more than once a year unless there has been a breach, during business hours, subject to confidentiality and without harming véltica’s security or other customers’ data. The Customer bears the cost of the audit.',
            ],
          },
        ],
      },
      {
        title: 'Subprocessors',
        blocks: [
          'The Customer gives general authorization for véltica to use subprocessors, which are listed on the [Subprocessors](doc:subprocessors) page. véltica will bind each subprocessor by a contract with data protection obligations no less protective than this DPA, and remains responsible for their performance.',
          'véltica will tell the Customer at least 30 days before adding or replacing a subprocessor, by updating the Subprocessors page and notifying by email or inside the Platform. The Customer may object on reasonable data protection grounds within 15 days. If the parties cannot resolve the objection, the Customer may stop using the affected part of the Platform and cancel it, and véltica will refund the prepaid fees for the period after the cancellation.',
        ],
      },
      {
        title: 'International transfers',
        blocks: [
          'véltica is established in the United States. Where the GDPR or the UK GDPR applies to a transfer of Customer Personal Data to véltica, the parties agree that the standard contractual clauses approved by the European Commission (Decision 2021/914) apply, Module Two (controller to processor), and, for onward transfers to subprocessors, Module Three, with these choices: the docking clause is omitted; the option for general authorization of subprocessors with a 30-day period applies; the optional wording of Clause 11 is omitted; the governing law and the courts are those of Ireland; and Annexes I, II and III are this DPA, Annex A and the Subprocessors page. For transfers subject to the UK GDPR, the UK International Data Transfer Addendum applies, and for transfers subject to Swiss law, the clauses apply with the references adapted to Swiss law. If a court or authority invalidates a transfer mechanism, the parties will use an alternative.',
        ],
      },
      {
        title: 'California and other US state laws',
        blocks: [
          'To the extent the CCPA or another US state privacy law applies, véltica acts as a service provider or processor and will: process Customer Personal Data only for the business purposes in the Agreement; not sell it or share it for cross-context behavioral advertising; not retain, use or disclose it outside the direct business relationship between the parties or for any purpose other than those business purposes; not combine it with personal data from other sources, except as the law allows; give the same level of privacy protection as the law requires; and tell the Customer if it can no longer meet these obligations. The Customer may take reasonable steps to make sure véltica uses the data consistently with the Customer’s obligations, and to stop and remediate unauthorized use.',
        ],
      },
      {
        title: 'The Customer’s obligations',
        blocks: [
          'The Customer is responsible for having a lawful basis and giving the notices required to collect and use Customer Personal Data, for its instructions being lawful, for configuring its Sites in line with the law, for answering data subjects’ requests, and for not collecting special categories of data or data that the Terms of Service prohibit.',
        ],
      },
      {
        title: 'General',
        blocks: [
          'This DPA lasts as long as véltica processes Customer Personal Data. It is subject to the limits of liability in the Terms of Service. If it conflicts with the Terms of Service on the processing of Customer Personal Data, this DPA prevails on that subject. If the standard contractual clauses apply and conflict with this DPA, the clauses prevail. Questions or requests under this DPA: {email}.',
        ],
      },
      {
        title: 'Annex A: security measures',
        blocks: [
          {
            list: [
              '**Encryption:** data is encrypted in transit with TLS, and our infrastructure providers encrypt data at rest.',
              '**Access control:** authentication for every account, least-privilege access for anyone working on the Platform, and restricted access to production systems.',
              '**Data separation:** each project’s data is separated from other customers’ data, including database access rules that limit each account to its own projects.',
              '**Payments:** card data is handled only by Stripe; véltica does not receive or store it.',
              '**Monitoring and logging:** security and error logs, and monitoring for abuse and attacks.',
              '**Backups and resilience:** regular backups of Site data, and infrastructure hosted with providers built for availability.',
              '**Secure development:** review of changes, dependency updates and separation of environments.',
              '**Incident response:** a process to investigate, contain and notify about security incidents.',
              '**Deletion:** deletion procedures that remove project data and its stored files after the restore period.',
            ],
          },
        ],
      },
    ],
  },

  es: {
    title: 'Acuerdo de procesamiento de datos',
    summary: 'Los términos con los que véltica trata por tu encargo los datos personales de tus visitantes, compradores y clientes.',
    intro: [
      'Este Acuerdo de procesamiento de datos (el «APD») forma parte de los [Términos de servicio](doc:terms) entre tú (el «Cliente») y {operator}, que opera bajo el nombre véltica («véltica»). Aplica en la medida en que véltica trata Datos personales del cliente por cuenta del Cliente al prestar la Plataforma. Refleja el acuerdo de las partes sobre el tratamiento de esos datos.',
    ],
    sections: [
      {
        title: 'Definiciones',
        blocks: [
          'Los «Datos personales del cliente» son los datos personales de Usuarios finales que véltica trata por cuenta del Cliente a través de la Plataforma, como los datos enviados por formularios, reservas y pedidos, las listas de contactos y las estadísticas de los Sitios del Cliente. Las «Leyes de protección de datos» son las leyes de privacidad y protección de datos que aplican a ese tratamiento, incluidos, cuando proceda, el Reglamento General de Protección de Datos de la UE («RGPD»), el RGPD del Reino Unido y la Data Protection Act 2018, la Ley Federal suiza de Protección de Datos y la Ley de Privacidad del Consumidor de California en su versión vigente («CCPA»). «Responsable», «encargado», «proveedor de servicios», «interesado», «tratamiento», «datos personales», «negocio» y «vender» tienen el sentido que les dan esas leyes.',
        ],
      },
      {
        title: 'Papeles y ámbito',
        blocks: [
          'Respecto de los Datos personales del cliente, el Cliente es el responsable del tratamiento (o «negocio») y véltica es el encargado (o «proveedor de servicios»). véltica es el responsable de los datos personales descritos en la [Política de privacidad](doc:privacy), que este APD no cubre.',
        ],
      },
      {
        title: 'Detalles del tratamiento',
        blocks: [
          {
            table: {
              head: ['Elemento', 'Descripción'],
              rows: [
                ['Objeto y finalidad', 'Prestar la Plataforma: alojar los Sitios del Cliente y almacenar, mostrar y entregar los datos que recogen'],
                ['Duración', 'Mientras el Cliente tenga una Cuenta, más los plazos de conservación de la Política de privacidad'],
                ['Interesados', 'Los visitantes de los Sitios del Cliente, compradores, personas que envían formularios o reservan, y contactos'],
                ['Tipos de datos', 'Datos de contacto, contenido de mensajes, datos de reservas, datos de pedidos y datos técnicos como país, tipo de dispositivo y visitas a páginas en agregado'],
                ['Categorías especiales', 'Ninguna prevista. El Cliente no debe usar la Plataforma para recogerlas (consulta los Términos de servicio)'],
                ['Operaciones', 'Almacenar, recuperar, transmitir, mostrar, enviar por correo y eliminar datos, según lo configure el Cliente'],
              ],
            },
          },
        ],
      },
      {
        title: 'Obligaciones de véltica',
        subs: [
          {
            title: 'Instrucciones',
            blocks: [
              'véltica tratará los Datos personales del cliente solo según las instrucciones documentadas del Cliente, que son el Acuerdo y el uso y la configuración que el Cliente haga de la Plataforma, salvo que la ley exija otra cosa, en cuyo caso véltica informará al Cliente cuando la ley lo permita. Si véltica cree que una instrucción infringe las Leyes de protección de datos, se lo dirá al Cliente.',
            ],
          },
          {
            title: 'Confidencialidad',
            blocks: [
              'véltica se asegurará de que las personas que traten Datos personales del cliente estén obligadas por deberes de confidencialidad.',
            ],
          },
          {
            title: 'Seguridad',
            blocks: [
              'véltica aplicará medidas técnicas y organizativas adecuadas al riesgo, al menos las del Anexo A.',
            ],
          },
          {
            title: 'Asistencia',
            blocks: [
              'Teniendo en cuenta la naturaleza del tratamiento, véltica ayudará razonablemente al Cliente a responder a las solicitudes de los interesados y a cumplir sus obligaciones de seguridad, notificación de brechas, evaluaciones de impacto y consulta previa. Si un interesado contacta a véltica sobre Datos personales del cliente, véltica lo dirigirá al Cliente cuando pueda.',
            ],
          },
          {
            title: 'Brechas de datos personales',
            blocks: [
              'véltica avisará al Cliente sin demora indebida, y procurará hacerlo en 72 horas, tras tener conocimiento de una brecha de datos personales que afecte a los Datos personales del cliente, con la información de que disponga para ayudar al Cliente a cumplir sus propias obligaciones.',
            ],
          },
          {
            title: 'Eliminación y devolución',
            blocks: [
              'Cuando termine el tratamiento, véltica eliminará los Datos personales del cliente como se describe en la Política de privacidad, incluso tras el periodo en que un proyecto eliminado se puede restaurar, salvo que la ley exija conservarlos. Antes de la eliminación, el Cliente puede guardar los datos que necesite.',
            ],
          },
          {
            title: 'Demostrar el cumplimiento',
            blocks: [
              'véltica pondrá a disposición la información razonablemente necesaria para demostrar su cumplimiento de este APD y permitirá y colaborará con auditorías razonables del Cliente o de su auditor, con aviso razonable, no más de una vez al año salvo que haya habido una brecha, en horario laboral, sujetas a confidencialidad y sin poner en riesgo la seguridad de véltica ni los datos de otros clientes. El Cliente asume el coste de la auditoría.',
            ],
          },
        ],
      },
      {
        title: 'Subprocesadores',
        blocks: [
          'El Cliente da una autorización general para que véltica use subprocesadores, que se enumeran en la página de [Subprocesadores](doc:subprocessors). véltica obligará a cada subprocesador mediante un contrato con obligaciones de protección de datos no menos protectoras que este APD, y sigue siendo responsable de su cumplimiento.',
          'véltica avisará al Cliente al menos 30 días antes de añadir o sustituir un subprocesador, actualizando la página de Subprocesadores y avisando por correo o dentro de la Plataforma. El Cliente puede oponerse por motivos razonables de protección de datos en 15 días. Si las partes no resuelven la objeción, el Cliente puede dejar de usar la parte afectada de la Plataforma y cancelarla, y véltica reembolsará las tarifas prepagadas correspondientes al periodo posterior a la cancelación.',
        ],
      },
      {
        title: 'Transferencias internacionales',
        blocks: [
          'véltica está establecida en Estados Unidos. Cuando el RGPD o el RGPD del Reino Unido apliquen a una transferencia de Datos personales del cliente a véltica, las partes acuerdan que aplican las cláusulas contractuales tipo aprobadas por la Comisión Europea (Decisión 2021/914), Módulo Dos (de responsable a encargado) y, para las transferencias ulteriores a subprocesadores, Módulo Tres, con estas opciones: se omite la cláusula de acoplamiento; aplica la opción de autorización general de subprocesadores con un plazo de 30 días; se omite el texto opcional de la Cláusula 11; la ley aplicable y los tribunales son los de Irlanda; y los Anexos I, II y III son este APD, el Anexo A y la página de Subprocesadores. Para las transferencias sujetas al RGPD del Reino Unido, aplica el Anexo del Reino Unido sobre transferencias internacionales de datos, y para las sujetas a la ley suiza, las cláusulas aplican con las referencias adaptadas a la ley suiza. Si un tribunal o autoridad invalida un mecanismo de transferencia, las partes usarán uno alternativo.',
        ],
      },
      {
        title: 'California y otras leyes estatales de EE. UU.',
        blocks: [
          'En la medida en que apliquen la CCPA u otra ley estatal de privacidad de EE. UU., véltica actúa como proveedor de servicios o encargado y: tratará los Datos personales del cliente solo para los fines comerciales del Acuerdo; no los venderá ni los compartirá para publicidad conductual entre contextos; no los conservará, usará ni divulgará fuera de la relación comercial directa entre las partes ni para fines distintos de esos fines comerciales; no los combinará con datos personales de otras fuentes, salvo lo que la ley permita; dará el mismo nivel de protección de la privacidad que la ley exige; y avisará al Cliente si ya no puede cumplir estas obligaciones. El Cliente puede tomar medidas razonables para asegurarse de que véltica usa los datos de forma coherente con las obligaciones del Cliente, y para detener y corregir un uso no autorizado.',
        ],
      },
      {
        title: 'Obligaciones del Cliente',
        blocks: [
          'El Cliente es responsable de tener una base jurídica y dar los avisos necesarios para recoger y usar los Datos personales del cliente, de que sus instrucciones sean lícitas, de configurar sus Sitios conforme a la ley, de atender las solicitudes de los interesados y de no recoger categorías especiales de datos ni datos que los Términos de servicio prohíban.',
        ],
      },
      {
        title: 'Disposiciones generales',
        blocks: [
          'Este APD dura mientras véltica trate Datos personales del cliente. Está sujeto a los límites de responsabilidad de los Términos de servicio. Si choca con los Términos de servicio en el tratamiento de Datos personales del cliente, prevalece este APD en ese tema. Si aplican las cláusulas contractuales tipo y chocan con este APD, prevalecen las cláusulas. Preguntas o solicitudes bajo este APD: {email}.',
        ],
      },
      {
        title: 'Anexo A: medidas de seguridad',
        blocks: [
          {
            list: [
              '**Cifrado:** los datos se cifran en tránsito con TLS, y nuestros proveedores de infraestructura los cifran en reposo.',
              '**Control de acceso:** autenticación en cada cuenta, acceso con el mínimo privilegio para quien trabaje en la Plataforma y acceso restringido a los sistemas de producción.',
              '**Separación de datos:** los datos de cada proyecto están separados de los de otros clientes, incluidas reglas de acceso a la base de datos que limitan cada cuenta a sus propios proyectos.',
              '**Pagos:** los datos de tarjeta los trata solo Stripe; véltica no los recibe ni los guarda.',
              '**Monitoreo y registros:** registros de seguridad y de errores, y vigilancia de abusos y ataques.',
              '**Copias y resiliencia:** copias de seguridad periódicas de los datos de los Sitios e infraestructura alojada con proveedores pensados para la disponibilidad.',
              '**Desarrollo seguro:** revisión de cambios, actualización de dependencias y separación de entornos.',
              '**Respuesta a incidentes:** un proceso para investigar, contener y notificar los incidentes de seguridad.',
              '**Eliminación:** procedimientos de eliminación que borran los datos de un proyecto y sus archivos almacenados tras el periodo de restauración.',
            ],
          },
        ],
      },
    ],
  },
}
